/**
 * /case-studies/ai-test-generation
 *
 * TWO SOURCES, reconciled — same rule as the chatbot page:
 *
 * - `ai-testing-use-cases.md`, use case 3 — the client's rewrite. Supplies the
 *   query-form heading, the SEO metadata, and the before/after results table.
 * - The live page, supplied as screenshots. Carries two full workflow diagrams
 *   the rewrite reduced to single sentences, the six approach items in their
 *   real order with their sub-points, and fuller intro copy.
 *
 * Where they overlap, the rewrite wins on wording. Where the original has
 * content the rewrite drops, the original is restored. Nothing is composed.
 *
 * RESULTS. The rewrite replaces the original's four bullets with a before/after
 * table and calls it "the results section the other two need". The table is
 * kept: it carries the same four claims in a form a reader can check.
 *
 * METRIC. "2–3 hours per feature → under 10 minutes per feature" is a measured
 * client outcome and is now on CLAUDE.md's approved list. Every other figure
 * here — 10 steps, 2 checkpoints, 4 sources, 8 checks — describes the agent,
 * not a client result.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "AI Test Generation — From Feature Ticket to Production-Ready Tests",
  description:
    "An AI agent reads your ticket, API spec, live UI and existing tests, then writes test code in your project's conventions. 2–3 hours down to under 10 minutes.",
} as const;

export const hero = {
  breadcrumb: { label: "Case Studies", href: "/case-studies" },
  badge: "Use Case #3",
  heading: "AI-Powered Test Generation for Enterprise Test Automation",
  lead: "It can write tests.",
  leadTurn: "Whether they run is a different question.",
  body: "Generic generation produces code from a template — the wrong structure for your project, mismatched conventions, a build that fails immediately, and hours of manual fixing before a single test executes. Two or three hours per feature, and the engineer needs deep framework knowledge anyway.",
} as const;

/** Restored to the live page's fuller wording. */
export const problem = {
  heading: "Can AI write automated tests?",
  display: "The problem with generated tests",
  client:
    "Our client manages a large enterprise platform built from dozens of independent services, maintained by multiple teams in parallel. Every new feature requires automated test coverage — and writing those tests manually is time-consuming, repetitive, and requires deep knowledge of a custom test automation framework with strict conventions.",
  built:
    "We built an AI agent that reads a feature ticket and delivers production-ready test code — correctly structured for the project, ready to run, and requiring no manual fixes.",
} as const;

/** Diagram 1, rebuilt from the live page. */
export const workflow = {
  title: "From Ticket to Test — AI-Powered Generation",
  subtitle:
    "An AI agent reads your project, understands the requirement, and writes ready-to-run tests",
  sourcesLabel: "Information sources",
  sources: [
    {
      label: "Feature Request",
      note: "Requirement and acceptance criteria",
      icon: "fileText",
    },
    { label: "API Spec", note: "Endpoint structure and data formats", icon: "code" },
    { label: "Live Interface", note: "Current UI elements and user flows", icon: "cube" },
    { label: "Existing Tests", note: "Project patterns and conventions", icon: "layers" },
  ] as { label: string; note: string; icon: IconName }[],
  stages: [
    {
      eyebrow: "AI Agent",
      title: "Understands and Plans",
      points: [
        "Reads all four sources simultaneously",
        "Builds a complete picture of what to test",
      ],
    },
    {
      eyebrow: "Checkpoint",
      title: "Engineer Reviews the Plan",
      points: ["Confirms what will be tested before any writing starts"],
    },
    {
      eyebrow: "Output",
      title: "Tests Written and Self-Checked by Agent",
      points: [
        "8 quality checks run automatically",
        "Engineer reviews",
        "Delivered ready to run",
      ],
    },
  ],
  old: {
    label: "Old Approach",
    value: "2–3 hours",
    unit: "per feature",
    points: [
      "Manual writing required",
      "Framework expertise needed",
      "Inconsistent quality",
    ],
    tag: "Traditional QA",
  },
  now: {
    label: "With AI Agent",
    value: "Under 10 min",
    unit: "per feature",
    points: [
      "Automated generation",
      "Works for any team member",
      "Consistent quality guaranteed",
    ],
    tag: "AI-powered QA",
  },
  caption: "AI-Powered Test Generation Workflow (10-step protocol)",
  altText:
    "Four information sources — the feature request, the API spec, the live interface and the existing tests — are read simultaneously by the AI agent, which understands and plans what to test. An engineer reviews the plan before any writing starts. The agent then writes the tests and self-checks them with eight automatic quality checks before an engineer reviews and they are delivered ready to run. The old approach took two to three hours per feature and needed framework expertise; with the agent it takes under ten minutes and works for any team member.",
} as const;

/** Diagram 2, rebuilt from the live page. Sits inside approach item 2. */
export const learns = {
  title: "Smart Test Generation — How the Agent Learns Your Project",
  subtitle:
    "Agent reads the real project first, then generates code that fits — no generic templates",
  old: {
    label: "Old Way: Generic Template",
    steps: [
      { title: "One-size-fits-all template", note: "Same structure for every project" },
      { title: "Mismatched structure", note: "Wrong patterns for this project" },
      { title: "Build fails immediately", note: "Engineer must fix manually" },
      { title: "Hours of rework", note: "Before any real test runs" },
    ],
    result: "2–3 hours per feature",
  },
  agent: {
    eyebrow: "AI Agent",
    title: "Reads Your Project First",
    reads: ["Existing tests", "Project config", "Team patterns", "Shared helpers"],
    note: "Extracts patterns automatically",
    understands: [
      "Understands your project structure",
      "Applies those patterns to new tests",
    ],
  },
  now: {
    label: "New Way: Project-Aware Agent",
    steps: [
      { title: "Reads the real project", note: "Learns how your team codes" },
      { title: "Matches your patterns exactly", note: "Style, structure, conventions" },
      { title: "Builds successfully first time", note: "No manual fixes needed" },
      { title: "Ready to run immediately", note: "Tests pass on first attempt" },
    ],
    result: "Under 10 minutes per feature",
  },
  band: {
    lead: "Works across every service and team — each project gets its own exact patterns, extracted live",
    note: "No outdated templates · No drift from real conventions · Identical to hand-written code",
    close:
      "Any engineer can generate correct, production-ready tests without knowing the framework internals",
  },
  caption: "Smart Test Generation — How the Agent Learns Your Project",
  altText:
    "The old way applies a one-size-fits-all template, which mismatches the project's structure, fails on first build and costs hours of rework — two to three hours per feature. The agent instead reads the real project first: existing tests, project config, team patterns and shared helpers, extracting patterns automatically. It then matches those patterns exactly, builds successfully first time and is ready to run immediately — under ten minutes per feature.",
} as const;

export type Approach = {
  title: string;
  body: string;
  points?: string[];
  stats?: { value: string; label: string }[];
  icon: IconName;
};

export const approach = {
  display: "Our Approach",
  items: [
    {
      title: "Agentic Workflow",
      body: "The entire workflow is triggered by a single command. The agent follows a structured 10-step protocol with two human approval checkpoints.",
      stats: [
        { value: "10", label: "Step protocol" },
        { value: "2", label: "Human approval checkpoints" },
      ],
      icon: "sequence",
    },
    {
      title: "Project-Aware Code Generation",
      body: "Rather than relying on hardcoded templates, the agent reads the actual project before generating code:",
      points: [
        "Scans existing tests to understand conventions",
        "Identifies correct structural patterns in use",
        "Finds shared utilities to reuse instead of duplicating",
        "Extracts naming and organization conventions",
      ],
      icon: "search",
    },
    {
      title: "Multi-Source Context Fusion",
      body: "Each test draws from four live sources:",
      points: [
        "Feature ticket (user story)",
        "API specification",
        "Live UI (interactive elements)",
        "Existing tests (patterns)",
      ],
      icon: "network",
    },
    {
      title: "Full Test Coverage",
      body: "Generates all required test types:",
      points: [
        "End-to-end flow tests",
        "Read and query validation",
        "Access control tests",
        "Error and edge cases",
      ],
      icon: "target",
    },
    {
      title: "Self-Review Quality Gate",
      body: "Before presenting code, the agent runs an 8-point quality check covering structure, coverage, cleanup, security, metadata, style, and error handling.",
      stats: [{ value: "8", label: "Point check before you see it" }],
      icon: "shieldCheck",
    },
    {
      title: "Session Resilience",
      body: "The agent saves generation plans. If interrupted, engineers can resume from where it left off with a single instruction.",
      icon: "infinity",
    },
  ] as Approach[],
  /** Diagram 2 belongs to item 2. */
  diagramAfterIndex: 1,
} as const;

export const results = {
  display: "Results",
  headline: {
    beforeLabel: "Old Approach",
    before: "2–3 hours",
    beforeNote: "per feature",
    afterLabel: "With AI Agent",
    after: "Under 10 min",
    afterNote: "per feature",
  },
  columns: { before: "Before", after: "After" },
  rows: [
    { before: "Framework expertise required", after: "Any team member can run it" },
    {
      before: "Inconsistent quality between authors",
      after: "Consistent by construction",
    },
    { before: "Manual fixes before first run", after: "Runs on first attempt" },
  ],
  close: "Every acceptance criterion maps to a test case.",
} as const;

export const applies = {
  display: "Where This Applies",
  items: [
    "Enterprise test automation frameworks",
    "Teams maintaining tests across many services",
    "Projects with strict test patterns",
    "Organizations reducing manual QA overhead",
  ],
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "End-to-End Testing of AI Chatbots at Scale",
      href: "/case-studies/ai-chatbot-testing",
    },
    {
      label: "Testing On-Device AI in a Privacy-First Mobile Application",
      href: "/case-studies/on-device-ai-testing",
    },
    {
      label: "Test Architecture & QA Platform Engineering",
      href: "/services/qa-architecture-engineering",
    },
  ],
} as const;
